import { REFERRAL_STATUS_META, type Referral, type ReferralStatus } from './referrals-store';

/**
 * The four status summary tiles (SCRUM-330) — v6 `.referrals-summary`:
 * left-border status color, Exo 2 count, mono uppercase label. Clicking a
 * tile toggles the status filter: the active tile lifts (bg-4 + shadow,
 * stronger borders), the others mute to 55% opacity. Counts ALWAYS reflect
 * the full set, never the filtered set.
 */
const TILE_ORDER: ReferralStatus[] = ['pending', 'reviewed', 'campaign_sent', 'denied'];

const TILE_BORDER: Record<ReferralStatus, string> = {
  pending: 'border-l-brand-primary',
  reviewed: 'border-l-status-reviewed',
  campaign_sent: 'border-l-status-success',
  denied: 'border-l-status-denied',
};

export interface ReferralSummaryTilesProps {
  referrals: readonly Referral[];
  activeFilter: ReferralStatus | null;
  onToggle: (status: ReferralStatus) => void;
}

export function ReferralSummaryTiles({
  referrals,
  activeFilter,
  onToggle,
}: ReferralSummaryTilesProps) {
  return (
    <div className="grid grid-cols-2 gap-3 min-[900px]:grid-cols-4">
      {TILE_ORDER.map((status) => {
        const meta = REFERRAL_STATUS_META[status];
        const count = referrals.filter((r) => r.status === status).length;
        const isActive = activeFilter === status;
        const isMuted = activeFilter !== null && !isActive;
        return (
          <button
            key={status}
            type="button"
            aria-pressed={isActive}
            title={isActive ? `Clear ${meta.label} filter` : `Show only ${meta.label}`}
            onClick={() => {
              onToggle(status);
            }}
            className={`rounded-tk-md block w-full border border-l-[3px] px-4 py-3 text-left transition-all ${TILE_BORDER[status]} ${
              isActive
                ? 'bg-bg-4 border-t-border-2 border-r-border-2 border-b-border-2 shadow-tk-sm'
                : isMuted
                  ? 'bg-bg-2 border-border-0 hover:bg-bg-3 opacity-55 hover:opacity-85'
                  : 'bg-bg-3 border-border-0 hover:bg-bg-4'
            }`}
          >
            <div className="font-display text-text-0 mb-1 text-2xl leading-none font-semibold">
              {count}
            </div>
            <div className="text-text-3 font-mono text-[0.625rem] font-semibold tracking-[0.08em] uppercase">
              {meta.label}
            </div>
          </button>
        );
      })}
    </div>
  );
}
